// SEO single source of truth (task 0006.05). Per-route, per-language meta + structured-data
// constants. Consumed by shared/seo/Seo.tsx, shared/seo/JsonLd.tsx and the build-time sitemap.
import { SITE } from "./site";
import type { Lang } from "../i18n/lang";
import { uz } from "../translations/uz";
import { ru } from "../translations/ru";

export const SITE_URL = SITE.domain; // https://nasiyah.uz — canonical/hreflang/JSON-LD: always prod

// 1200×630 per-language social card, rendered from src/og/ into public/og/ (see landing.md §9).
// The share image must be an absolute URL that actually resolves from the host serving the
// page. Canonical stays prod (above), but a staging mirror may run before prod has the asset,
// so the og:image base is overridable: VITE_OG_BASE (set to https://staging.nasiyah.uz in the
// staging build) makes the preview load from staging; unset → prod, so prod is unchanged.
const OG_BASE = (import.meta.env.VITE_OG_BASE as string | undefined)?.trim() || SITE_URL;
export const ogImage = (lang: Lang) => `${OG_BASE}/og/og-${lang}.png`;
export const OG_IMAGE_TYPE = "image/png";
export const OG_IMAGE_W = 1200;
export const OG_IMAGE_H = 630;

// Brand slogan; the single source is `common.slogan` in the translation files.
export const SLOGAN: Record<Lang, string> = { uz: uz.common.slogan, ru: ru.common.slogan };

export const OG_LOCALE: Record<Lang, string> = { uz: "uz_UZ", ru: "ru_RU" };

// Structured postal address (Organization JSON-LD). Language-neutral.
export const SITE_ADDRESS = {
  streetAddress: "Sag'bon ko'chasi 23A",
  addressLocality: "Toshkent",
  addressRegion: "Toshkent",
  addressCountry: "UZ",
} as const;

// Canonical prerendered paths (uz canonical form, no /ru prefix). The build sitemap and the
// Seo/hreflang layer both iterate this list.
export const ROUTE_PATHS = [
  "/",
  "/business-app/privacy",
  "/business-app/terms",
  "/client-app/privacy",
  "/client-app/terms",
] as const;
export type RoutePath = (typeof ROUTE_PATHS)[number];

// ogTitle overrides og:title / twitter:title only; <title> stays the search title.
type Meta = { title: string; description: string; ogTitle?: string };

// Per-route, per-language <title> + meta description. SEO surface strings live here (route-tied,
// not in the i18n UI bundle); subtask 06 may refine wording.
export const ROUTE_META: Record<RoutePath, Record<Lang, Meta>> = {
  "/": {
    uz: {
      title: "Nasiyah — nasiya savdo va to'lovlarni boshqarish ilovasi",
      ogTitle: SLOGAN.uz,
      description:
        "Qarz daftari, raqamli shartnoma va to'lov nazorati bitta ilovada. Nasiyah savdogarlarga nasiya savdoni qog'ozsiz, xatosiz va real vaqtda boshqarishga yordam beradi.",
    },
    ru: {
      title: "Nasiyah — приложение для рассрочки и учёта платежей",
      ogTitle: SLOGAN.ru,
      description:
        "Долговая книга, цифровой договор и контроль платежей в одном приложении. Nasiyah помогает продавцам вести рассрочку без бумаг, без ошибок и в реальном времени.",
    },
  },
  "/business-app/privacy": {
    uz: {
      title: "Maxfiylik siyosati — Nasiyah Biznes",
      description:
        "Nasiyah Biznes ilovasining maxfiylik siyosati: qanday ma'lumotlar yig'iladi, qanday saqlanadi va himoyalanadi.",
    },
    ru: {
      title: "Политика конфиденциальности — Nasiyah Бизнес",
      description:
        "Политика конфиденциальности приложения Nasiyah Бизнес: какие данные собираются, как хранятся и защищаются.",
    },
  },
  "/business-app/terms": {
    uz: {
      title: "Ommaviy oferta — Nasiyah Biznes",
      description: "NASIYAH dasturidan foydalanish yuzasidan ommaviy oferta — xizmat ko'rsatish shartlari (O'zR FK 832-modda).",
    },
    ru: {
      title: "Публичная оферта — Nasiyah Бизнес",
      description: "Публичная оферта на использование программы NASIYAH — условия оказания услуг (ст. 832 ГК РУз).",
    },
  },
  "/client-app/privacy": {
    uz: {
      title: "Maxfiylik siyosati — Nasiyah (mijoz)",
      description:
        "Nasiyah mijoz ilovasining maxfiylik siyosati va shaxsiy ma'lumotlarni himoya qilish qoidalari.",
    },
    ru: {
      title: "Политика конфиденциальности — Nasiyah (клиент)",
      description:
        "Политика конфиденциальности клиентского приложения Nasiyah и правила защиты персональных данных.",
    },
  },
  "/client-app/terms": {
    uz: {
      title: "Foydalanish shartlari — Nasiyah (mijoz)",
      description: "Nasiyah mijoz ilovasidan foydalanish shartlari.",
    },
    ru: {
      title: "Условия использования — Nasiyah (клиент)",
      description: "Условия использования клиентского приложения Nasiyah.",
    },
  },
};
